// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SignatureService } from '@/services/signatureService';
import { EmailService } from '@/services/emailService';
import { SignatureAssinatura } from '@/types/signature';
import { 
  CheckCircle, 
  XCircle, 
  Mail, 
  FileText, 
  User, 
  Calendar, 
  Loader2,
  AlertTriangle,
  Download
} from 'lucide-react';

export const EmailConfirmationSimple: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [assinatura, setAssinatura] = useState<SignatureAssinatura | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [step, setStep] = useState<'loading' | 'confirm' | 'success' | 'error'>('loading');

  useEffect(() => {
    if (token) {
      loadSignature();
    }
  }, [token]);

  const loadSignature = async () => {
    try {
      setLoading(true);
      const sig = await SignatureService.getSignatureByToken(token!);
      
      if (!sig) {
        setError('Token de confirmação inválido ou expirado.');
        setStep('error');
        return;
      }

      if (sig.status === 'confirmado') {
        setError('Esta assinatura já foi confirmada.');
        setStep('error');
        return;
      }

      setAssinatura(sig);
      setStep('confirm');
    } catch (err) {
      console.error('Erro ao carregar assinatura:', err);
      setError('Erro ao carregar dados da assinatura. Tente novamente.');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignature = async () => {
    if (!token) return;

    try {
      setConfirming(true);
      
      // Confirmar assinatura
      const confirmedSignature = await SignatureService.confirmSignature(token);
      
      // Marcar documento como assinado
      await SignatureService.markDocumentAsSigned(confirmedSignature.documento_id);
      
      // Enviar cópia do documento assinado por email
      if (confirmedSignature.url_pdf_assinado) {
        await EmailService.sendSignedDocumentCopy(
          confirmedSignature.email_assinante,
          confirmedSignature.nome_assinante,
          'Documento Assinado', // Você pode buscar o título do documento se necessário
          confirmedSignature.url_pdf_assinado
        );
      }

      setSuccess('Assinatura confirmada com sucesso! Uma cópia do documento foi enviada para seu email.');
      setStep('success');

    } catch (err) {
      console.error('Erro ao confirmar assinatura:', err);
      setError('Erro ao confirmar assinatura. Tente novamente.');
    } finally {
      setConfirming(false);
    }
  };

  const handleDownloadPDF = () => {
    if (assinatura?.url_pdf_assinado) {
      const link = document.createElement('a');
      link.href = assinatura.url_pdf_assinado;
      link.download = `documento_assinado_${Date.now()}.pdf`;
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Carregando dados da assinatura...</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Erro na Confirmação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button 
              onClick={() => navigate('/')} 
              className="w-full mt-4"
            >
              Voltar ao Início
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-green-600 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              Confirmação Realizada
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertDescription>{success}</AlertDescription>
            </Alert>
            {assinatura?.url_pdf_assinado && (
              <Button 
                onClick={handleDownloadPDF}
                className="w-full mt-4"
                variant="outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar Documento Assinado
              </Button>
            )}
            <Button 
              onClick={() => navigate('/')} 
              className="w-full mt-2"
            >
              Voltar ao Início
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!assinatura) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Assinatura Não Encontrada
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>Não foi possível encontrar os dados da assinatura.</AlertDescription>
            </Alert>
            <Button 
              onClick={() => navigate('/')} 
              className="w-full mt-4"
            >
              Voltar ao Início
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Confirmação de Assinatura
          </h1>
          <p className="text-gray-600">
            Confirme sua assinatura para finalizar o processo
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Informações da Assinatura */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              Detalhes da Assinatura
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Nome do Assinante:</label>
                  <p className="text-gray-900">{assinatura.nome_assinante}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Email:</label>
                  <p className="text-gray-900">{assinatura.email_assinante}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Data da Assinatura:</label>
                  <p className="text-gray-900">
                    {new Date(assinatura.data_assinatura).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">IP:</label>
                  <p className="text-gray-900">{assinatura.ip_assinante}</p>
                </div>
              </div>

              {assinatura.url_pdf_assinado && (
                <div className="pt-4 border-t">
                  <Button 
                    onClick={handleDownloadPDF}
                    variant="outline"
                    className="w-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Documento Assinado
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Botão de Confirmação */}
        <Card>
          <CardHeader>
            <CardTitle>Confirmar Assinatura</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Ao confirmar, você estará validando esta assinatura eletrônica e uma cópia do documento será enviada para seu email.
            </p>
            <Button 
              onClick={handleConfirmSignature}
              disabled={confirming}
              className="w-full"
            >
              {confirming ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Confirmando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar Assinatura
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
